import { useCallback, useEffect, useMemo } from "react";
import { useFavorites } from "./useFavorites";
import { useMovieCatalog } from "./useMovieCatalog";
import { useSelectedMovie } from "./useSelectedMovie";

/**
 * Punto de entrada único para App.js: compone los hooks de dominio
 * (catálogo, selección/trailer, favoritos) sin mezclar sus responsabilidades
 * internamente. Cada uno vive en su propio archivo y se puede testear aislado.
 */
export function useMovies() {
  const catalog = useMovieCatalog();
  const selection = useSelectedMovie();
  const favoritesState = useFavorites();

  const { selectMovie } = selection;
  const { loadInitialMovies, searchMovies: searchCatalog } = catalog;
  const { isFavoriteId } = favoritesState;

  const selectFirstMovie = useCallback(
    (movieList) => selectMovie(movieList.length ? movieList[0] : null),
    [selectMovie]
  );

  useEffect(() => {
    loadInitialMovies().then(selectFirstMovie);
  }, [loadInitialMovies, selectFirstMovie]);

  const searchMovies = useCallback(
    async (searchTerm) => {
      const results = await searchCatalog(searchTerm);
      await selectFirstMovie(results);
    },
    [searchCatalog, selectFirstMovie]
  );

  const isFavorite = useMemo(
    () => (selection.selectedMovie ? isFavoriteId(selection.selectedMovie.id) : false),
    [selection.selectedMovie, isFavoriteId]
  );

  const addFavorite = useCallback(() => {
    favoritesState.addFavorite(selection.selectedMovie);
  }, [favoritesState, selection.selectedMovie]);

  const removeFavorite = useCallback(() => {
    if (!selection.selectedMovie) return;
    favoritesState.removeFavorite(selection.selectedMovie.id);
  }, [favoritesState, selection.selectedMovie]);

  return {
    movies: catalog.movies,
    loading: catalog.loading,
    searchLoading: catalog.searchLoading,
    error: catalog.error,
    selectedMovie: selection.selectedMovie,
    trailer: selection.trailer,
    selectMovie: selection.selectMovie,
    favorites: favoritesState.favorites,
    isFavorite,
    addFavorite,
    removeFavorite,
    searchMovies,
  };
}
